// src/views/bots/questions/BaseQuestion.tsx
// 🎨 BASE QUESTION COMPONENT - Enhanced with Collapsible Features

import React, { useState, useEffect } from 'react';
import styles from './Questions.module.css';
import { QuestionItem } from '../hooks/useBotForm';
import logger from '../../../utils/logger';

interface BaseQuestionProps {
  question: QuestionItem;
  index: number;
  onUpdate: (id: string, field: keyof QuestionItem, value: any) => void;
  onRemove: (id: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  children: React.ReactNode; // Custom question type content
  forceCollapsed?: boolean; // Global collapse state from parent
}

const BaseQuestion: React.FC<BaseQuestionProps> = ({
  question,
  index,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  children,
  forceCollapsed
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Individual state always takes precedence - no overrides, user controls each question
  const effectiveCollapsed = isCollapsed;
  
  // When global Collapse All/Expand All is triggered, update individual state
  useEffect(() => {
    if (forceCollapsed !== undefined) {
      setIsCollapsed(forceCollapsed);
    }
  }, [forceCollapsed]);
  
  logger.log(`📝 BaseQuestion: Rendering question ${index + 1} (${question.type}):`, question.id);

  // 🔥 QUESTION TYPE INFO: Enhanced with colors and icons
  const questionTypeInfo = {
    'text': { label: 'Text Input', icon: '📝', color: '#3b82f6' },
    'email': { label: 'Email Address', icon: '📧', color: '#10b981' },
    'phone': { label: 'Phone Number', icon: '📱', color: '#f59e0b' },
    'number': { label: 'Number Input', icon: '🔢', color: '#8b5cf6' },
    'interactive-list': { label: 'Interactive List', icon: '📋', color: '#06b6d4' },
    'interactive-button': { label: 'Interactive Buttons', icon: '🔘', color: '#ec4899' },
    'location': { label: 'Location Sharing', icon: '📍', color: '#ef4444' },
    'template': { label: 'Template Message', icon: '📄', color: '#6b7280' },
  } as const;

  const typeInfo = questionTypeInfo[question.type] || questionTypeInfo['text'];

  // 🔥 QUESTION SUMMARY: For collapsed view
  const getQuestionSummary = () => {
    if (!question.question.trim()) {
      return 'No question text entered';
    }
    if (question.question.length > 50) {
      return question.question.substring(0, 50) + '...';
    }
    return question.question;
  };

  return (
    <div className={styles.questionItem}>
      
      {/* 🎯 QUESTION HEADER - Enhanced with Collapse and Type Info */}
      <div className={styles.questionHeader}>
        <div className={styles.questionNumber}>
          Q{index + 1}
        </div>
        
        <div className={styles.questionMeta}>
          <div 
            className={styles.questionType}
            style={{ backgroundColor: typeInfo.color, color: 'white' }}
          >
            {typeInfo.icon} {typeInfo.label}
          </div>
          
          {/* 🔥 QUESTION SUMMARY: Show when collapsed */}
          {effectiveCollapsed && (
            <div className={styles.questionSummary}>
              {getQuestionSummary()}
            </div>
          )}
        </div>

        <div className={styles.questionActions}>
          {/* 🔥 COLLAPSE TOGGLE - Always enabled, individual control */}
          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`${styles.actionBtn} ${styles.collapseBtn}`}
            title={effectiveCollapsed ? 'Expand question' : 'Collapse question'}
          >
            <i className={`fas fa-chevron-${effectiveCollapsed ? 'down' : 'up'}`}></i>
          </button>
          
          {/* 🔥 MOVE BUTTONS */}
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            className={`${styles.actionBtn} ${styles.moveUpBtn}`}
            title="Move question up"
          >
            <i className="fas fa-arrow-up"></i>
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            className={`${styles.actionBtn} ${styles.moveDownBtn}`}
            title="Move question down"
          >
            <i className="fas fa-arrow-down"></i>
          </button>
          
          {/* 🔥 REMOVE BUTTON */}
          <button
            type="button"
            onClick={() => onRemove(question.id)}
            className={`${styles.actionBtn} ${styles.removeBtn}`}
            title="Remove question"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>

      {/* 🎯 QUESTION CONTENT - Collapsible */}
      {!effectiveCollapsed && (
        <div className={styles.questionContent}>
          
          {/* 🔥 MAIN QUESTION INPUT */}
          {/* 🔥 MAIN QUESTION INPUT - Hide for template questions */}
{question.type !== 'template' && (
  <div className={styles.questionInputContainer}>
    <label className={styles.questionLabel}>Question:</label>
    <textarea
      value={question.question}
      onChange={(e) => onUpdate(question.id, 'question', e.target.value)}
      placeholder="Enter your question here..."
      className={styles.questionInput}
      rows={4}
    />
  </div>
)}

{/* 🔥 TEMPLATE QUESTIONS: Show template info instead */}
{question.type === 'template' && (
  <div className={styles.templateQuestionInfo}>
    <div className={styles.templateQuestionLabel}>
      <i className="fas fa-info-circle" style={{ color: '#3b82f6' }}></i>
      <span>Template Question</span>
    </div>
    <p className={styles.templateQuestionDescription}>
      This question will send a WhatsApp template message. Configure the template below.
    </p>
  </div>
)}


          {/* 🎯 TYPE-SPECIFIC CONTENT: Rendered by child components */}
          {children}
          
        </div>
      )}
    </div>
  );
};

export default BaseQuestion;